import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Paper, Box, Button, RadioGroup, FormControlLabel, Radio, FormControl, Select, MenuItem, FormHelperText, Card, CardContent, Divider, Grid, TextField } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SpeedIcon from "@mui/icons-material/Speed";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CommentIcon from "@mui/icons-material/Comment";
import PlatformIcon from "@mui/icons-material/Devices";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import * as styles from "../BackofficeRequest/Styles.js";
import { Spaces } from "../../assets/Variables/SpaceVariables.js";
import * as TextVariables from "../../assets/Variables/TextVariables.js";
import Colors from "../../assets/Variables/ColorsVariables.js";
import MoneyCard from "../../components/Cards/MoneyCard.js";
import NotificationModal from "../../components/Notifications/NotificationModal.js";
import { GetServersdata, RemoveServersData, SetServersdata } from "../../Tools/LocalStorage.js";
import ServersRequest from "../../Endpoints/ServersRequest.js";
import DealTreeRequest from "../../Endpoints/DealTreeRequest.js";

const FOREX_PLANS = [
  {
    id: "ILC",
    label: "Innovative Liquidity Connector (iLC)",
    spreads: "Spreads starting at 0.0",
    commission: "$5 per standard round turn lot",
    rebate: "$2.50 per standard round turn lot",
    leverage: "Max 1:200",
    tradingVolume: "Min 0.1 – Max 500.0 lots",
    summaryValue: "2.50/lot",
    summaryAsset: "ILC Rebate",
    subOptions: null,
  },
  {
    id: "XLEVERAGE",
    label: "X Leverage (XLev)",
    spreads: "Spreads starting at 0.7",
    commission: null,
    rebate: null,
    leverage: "Max 1:500",
    tradingVolume: "Min 0.01 – Max 500.0 lots",
    summaryValue: null,
    summaryAsset: "X Leverage",
    subOptions: {
      type: "commissionModel",
      options: [
        {
          id: "rebate",
          label: "Commission free with a $2.50 rebate per standard round turn lot",
          summaryValue: "2.50/lot",
          commission: "Free",
          rebate: "$2.50 per standard round turn lot"
        },
        {
          id: "upfront",
          label: "IB can charge an upfront commission (select amount)",
          summaryValue: (amount) => `${amount}/lot`,
          commission: "Free",
          rebate: (amount) => `$${amount} :: upfront commission`,
          allowedAmounts: ["5.00", "6.00", "7.00", "8.00", "9.00", "10.00"],
        },
      ],
    },
  },
];

const EQUITIES_PLANS = [
  {
    id: "MetaTrader 5",
    label: "MetaTrader 5",
    description: "A penny per share they get 20%. For anything above one penny per share, the IB and the firm split the profit 50-50.",
    summaryValue: "20% / 50-50 split",
    badge: "MT5",
  },
  {
    id: "STERLING",
    label: "Sterling",
    description: "The IB gets 20% of the net profits the firm makes.",
    summaryValue: "20% of net profits",
    badge: "Sterling",
  },
];

const CURRENCY_OPTIONS = [
  { id: "USD", label: "USD"},
  { id: "EUR", label: "EUR"},
  { id: "JPY", label: "JPY"},
  { id: "GBP", label: "GBP"},
  { id: "CAD", label: "CAD"},
  { id: "AUD", label: "AUD"},
  { id: "CHF", label: "CHF"}
];

const getForexPlanById = (id) => FOREX_PLANS.find(p => p.id === id);
const getEquitiesPlanById = (id) => EQUITIES_PLANS.find(p => p.id === id);
const getSubOptionById = (plan, subOptionId) => {
  if (!plan?.subOptions) return null;
  return plan.subOptions.options.find(opt => opt.id === subOptionId);
};

function DealTreeRequestPage() {
  const navigate = useNavigate();
  const [selectedProductType, setSelectedProductType] = useState("forex");
  const [selectedForexPlanId, setSelectedForexPlanId] = useState("ILC");
  const [selectedSubOptionId, setSelectedSubOptionId] = useState("rebate");
  const [upfrontCommissionAmount, setUpfrontCommissionAmount] = useState("5.00");
  const [selectedEquitiesPlanId, setSelectedEquitiesPlanId] = useState("MetaTrader 5");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [comment, setComment] = useState("");
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalProps, setModalProps] = useState({
    iconType: "",
    title: "",
    message: "",
    onAcceptLabel: undefined,
    onRefuseLabel: undefined,
    onAccept: undefined,
    onRefuse: undefined,
  });
  const [servers, setServers] = useState([]);

  const currentForexPlan = getForexPlanById(selectedForexPlanId);
  const currentSubOption = getSubOptionById(currentForexPlan, selectedSubOptionId);
  const currentEquitiesPlan = getEquitiesPlanById(selectedEquitiesPlanId);

  const hasSubOptions = currentForexPlan?.subOptions !== null;
  const isUpfrontSelected = hasSubOptions && selectedSubOptionId === "upfront";
  const upfrontAllowedAmounts = isUpfrontSelected
    ? currentForexPlan.subOptions.options.find(opt => opt.id === "upfront")?.allowedAmounts || [5,6,7,8,9,10]
    : [];

  useEffect(() => {
    const serversData = GetServersdata();
    if(serversData.length > 0){
      const orderedServers = orderServers(serversData);
      setServers(orderedServers)
      setSelectedPlatform(orderedServers[0].value);
    }
    else{
      RequestServersData();
    }
  }, []);

  const RequestServersData = async () => {
    try{
      const response = await ServersRequest();
      if (response.data.data != null) {
        let actualRows = response.data.data.servers
        .filter(server => server.includes("LTD"))
        .map((server, index) => {
          let row = {
              label: server,
              value: server
          }
          return row;
        });
        const orderedServers = orderServers(actualRows);
        setServers(orderedServers)
        setSelectedPlatform(orderedServers[0].value);
        SetServersdata(orderedServers)
      }
      else{
        setServers([])
        RemoveServersData();
      }
    }
    catch(error){
      setServers([])
      RemoveServersData();
      console.log(error);
    }
  }  
  
  const orderServers = (servers) => {
    const typeOrder = ["LIVE", "DEMO", "TEST"];

    return [...servers].sort((a, b) => {
      const A = a.label.toUpperCase();
      const B = b.label.toUpperCase();

      // LTD first
      const aLTD = A.includes("LTD") ? 0 : 1;
      const bLTD = B.includes("LTD") ? 0 : 1;
      if (aLTD !== bLTD) return aLTD - bLTD;

      // MT4 and MT5 in order
      const aMT = A.includes("MT4") ? 4 : 5;
      const bMT = B.includes("MT4") ? 4 : 5;
      if (aMT !== bMT) return aMT - bMT;

      // Live and Demo in order
      const aType = typeOrder.findIndex(t => A.includes(t));
      const bType = typeOrder.findIndex(t => B.includes(t));
      if (aType !== bType) return aType - bType;

      // number organization
      const aNum = parseInt(A.match(/\d+$/)?.[0] || "0");
      const bNum = parseInt(B.match(/\d+$/)?.[0] || "0");

      return aNum - bNum;
    })
  };

  const getCurrentCommission = () => {
    if (selectedForexPlanId === "ILC") {
      return currentForexPlan.commission;
    }
    if (currentSubOption) {
      if (typeof currentSubOption.commission === "function") {
        return currentSubOption.commission(upfrontCommissionAmount);
      }
      return currentSubOption.commission;
    }
    return "Select option";
  };

  const getCurrentRebate = () => {
    if (selectedForexPlanId === "ILC") {
      return currentForexPlan.rebate;
    }
    if (currentSubOption) {
      if (typeof currentSubOption.rebate === "function") {
        return currentSubOption.rebate(upfrontCommissionAmount);
      }
      return currentSubOption.rebate;
    }
    return "Select option";
  };

  const getSummaryValue = () => {
    if (selectedProductType === "forex") {
      if (selectedForexPlanId === "ILC") {
        return currentForexPlan.summaryValue;
      }
      if (currentSubOption) {
        if (typeof currentSubOption.summaryValue === "function") {
          return currentSubOption.summaryValue(upfrontCommissionAmount);
        }
        return currentSubOption.summaryValue;
      }
    } else {
      return currentEquitiesPlan.summaryValue;
    }
    return "Select option";
  };

  const handleClickToBackPage = () => navigate("/requests");
  const handleCloseNotification = () => setShowNotification(false);

  const isUpfrontValid = () => {
    if (isUpfrontSelected) {
      return upfrontCommissionAmount != null && upfrontAllowedAmounts.includes(upfrontCommissionAmount);
    }
    return true;
  };

  const canSubmit = !submitting && isUpfrontValid() && selectedPlatform;

  const buildSubmissionSummary = () => {
    if (selectedProductType === "forex") {
      return (
        <>
          • <strong>Selected Product:</strong> Forex/CFD<br/>
          • <strong>Plan:</strong> {currentForexPlan.label}<br/>
          • <strong>Spreads:</strong> {currentForexPlan.spreads}<br/>
          • <strong>Commission:</strong> {getCurrentCommission()}<br/>
          • <strong>Rebate:</strong> {getCurrentRebate()}<br/>
          • <strong>Leverage:</strong> {currentForexPlan.leverage}<br/>
          • <strong>Trading Volume:</strong> {currentForexPlan.tradingVolume}<br/>
          • <strong>Platform:</strong> {servers.find(p => p.value === selectedPlatform)?.label}<br/>
          • <strong>Currency:</strong> {selectedCurrency}<br/>
          <br/>
          <strong>Comment:</strong> {comment || "No comment"}
        </>
      );
    }

    return (
      <>
        <strong>Selected Product:</strong> Equities<br/>
        <strong>Plan:</strong> {currentEquitiesPlan.label}<br/>
        • <strong>Structure:</strong> {currentEquitiesPlan.description}<br/>
        • <strong>Rebate:</strong> {currentEquitiesPlan.summaryValue}<br/>
        <br/>
        <strong>Comment:</strong> {comment || "No comment"}
      </>
    );
  }; 

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await DealTreeRequest({
        market: selectedProductType === "forex" ? "Forex/CFD" : "Equities",
        type: selectedProductType === "forex" ? selectedForexPlanId : selectedEquitiesPlanId,
        commission: selectedProductType === "forex" ? getCurrentCommission() : null,
        rebate: selectedProductType === "forex" ? getCurrentRebate() : currentEquitiesPlan.summaryValue,
        platform: selectedProductType === "forex" ? servers.find(p => p.value === selectedPlatform)?.label || selectedPlatform : "MT5 LTD Live 1",
        currency: selectedProductType === "forex" ? selectedCurrency : "USD",
        comment: comment.trim() ?? "No comments",
      });
      if (response && response.status === 200) {
        setModalProps({
          iconType: "success",
          title: "Request Submitted Successfully",
          message: "Your deal tree request has been sent.",
          onAcceptLabel: "Continue",
          onAccept: () => {
            setShowNotification(false);
            navigate("/requests");
          },
        });
       }
       else{
          setModalProps({
            iconType: "warning",
            title: "Error in request",
            message: "Please try later",
            onAcceptLabel: "Try again",
            onRefuseLabel: "Close",
            onAccept: () => handleOnAccept(),
            onRefuse: () => handleOnRefuse(),
          });
        }

        setShowNotification(true);
    }
    catch (error) {
      setModalProps({
        iconType: "error",
        title: "Error",
        message: error?.message || "An error occurred. Please try again later.",
        onAcceptLabel: "Close",
        onAccept: () => setShowNotification(false),
      });
      setShowNotification(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    setSubmittedOnce(true);
    if (!canSubmit) {
      setModalProps({
        iconType: "warning",
        title: "Incomplete Information",
        message: "Please fill in all required fields before submitting.",
        onAcceptLabel: "Close",
        onAccept: () => setShowNotification(false),
      });
      setShowNotification(true);
      return;
    }

    setModalProps({
      iconType: "warning",
      title: "Confirm Your Selection",
      message: (
        <>
          {buildSubmissionSummary()}
        </>
      ),
      onRefuseLabel: "Cancel",
      onAcceptLabel: "Confirm Submission",
      onRefuse: () => setShowNotification(false),
      onAccept: () => {
        setShowNotification(false);
        handleConfirmSubmit();
      },
    });
    setShowNotification(true);
  };

  return (
    <Box sx={styles.mainBox}>
      <NotificationModal
        open={showNotification}
        onClose={handleCloseNotification}
        iconType={modalProps.iconType}
        title={modalProps.title}
        message={modalProps.message}
        onAcceptLabel={modalProps.onAcceptLabel}
        onRefuseLabel={modalProps.onRefuseLabel}
        onAccept={modalProps.onAccept}
        onRefuse={modalProps.onRefuse}
      />

      <Box sx={styles.secondBox}>
        <Box sx={styles.backButtonContainer}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button
              onClick={handleClickToBackPage}
              startIcon={<ArrowBackIosIcon style={{ fontSize: 10 }} />}
              sx={{ textTransform: "none", color: "black" }}
            >
              Go back
            </Button>
          </Box>
        </Box>

        <Paper
          elevation={2}
          sx={{
            ...(styles.mainPaper || {}),
            borderRadius: "12px",
            padding: Spaces.Size_5,
            width: "1300px",
            minWidth: "1200px",
            margin: "0 auto",
          }}
        >
          <Box>
            <Typography sx={{ fontFamily: TextVariables.Body_3_Bold, fontSize: "1.5rem" }}>
              Deal Tree Request
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card 
                  sx={styles.productCards(selectedProductType, 'forex')}
                  onClick={() => setSelectedProductType('forex')}
                >
                  {selectedProductType === 'forex' && ( <Box sx={styles.selectedProductCardBox} />)}
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      <TrendingUpIcon /> 
                      Forex / CFD
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Trade in the foreign exchange and CFD markets
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ mt: 2, display: 'block' }}>
                      Spreads from 0.0 • Leverage up to 1:500
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card 
                  sx={styles.productCards(selectedProductType, 'equities')}
                  onClick={() => setSelectedProductType('equities')}
                >
                  {selectedProductType === 'equities' && ( <Box sx={styles.selectedProductCardBox} />)}
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      <SwapHorizIcon /> Equities
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Trade in the stock market
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ mt: 2, display: 'block' }}>
                      Commissions from 0 • Attractive rebates
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4} sx={{ borderRight: { md: `1px solid ${Colors.Gray_300}` }, pr: { md: 3 } }}>
              <MoneyCard
                title="Your Rebate Summary"
                value={getSummaryValue()}
                asset={selectedProductType === "forex" ? "USD" : null}
              />
              
              {selectedProductType === "forex" ? (
                <Card sx={{ mt: 2, borderRadius: "12px", overflow: "hidden", boxShadow: 1 }}>
                  <Box sx={{ bgcolor: Colors.Primary_600, px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
                      Current Plan Details
                    </Typography>
                  </Box>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <TrendingUpIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Spreads:</strong> {currentForexPlan.spreads}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <AttachMoneyIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Commission:</strong> {getCurrentCommission()}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <SwapHorizIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Rebate:</strong> {getCurrentRebate()}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <SpeedIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Leverage:</strong> {currentForexPlan.leverage}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <ShowChartIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Trading Volume:</strong> {currentForexPlan.tradingVolume}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PlatformIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Platform:</strong> {servers.find(p => p.value === selectedPlatform)?.label}
                        </Typography>
                      </Box>
                      
                      <Divider />
                      
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CurrencyExchangeIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Currency:</strong> {selectedCurrency}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Card sx={{ mt: 2, borderRadius: "12px", bgcolor: Colors.Gray_50, boxShadow: 1 }}>
                  <Box sx={{ bgcolor: Colors.Primary_600, px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: "white", fontWeight: "bold" }}>
                      Current Plan Details
                    </Typography>
                  </Box>
                  <CardContent>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="body2">
                        <strong>Plan:</strong> {currentEquitiesPlan.label}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Structure:</strong> {currentEquitiesPlan.description}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Rebate:</strong> {currentEquitiesPlan.summaryValue}
                      </Typography>
                      <Divider />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CurrencyExchangeIcon sx={{ fontSize: 18, color: Colors.Primary_600 }} />
                        <Typography variant="body2">
                          <strong>Currency:</strong> USD
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={8}>
              {selectedProductType === "forex" && (
                <Box sx={{ mb: 4 }}>
                  <RadioGroup value={selectedForexPlanId} onChange={(e) => setSelectedForexPlanId(e.target.value)}>
                    <Grid container spacing={2}>
                      {FOREX_PLANS.map(plan => (
                        <Grid item xs={6} key={plan.id}>
                          <Card sx={{ 
                            border: selectedForexPlanId === plan.id ? `2px solid ${Colors.Primary_600}` : `1px solid ${Colors.Gray_300}`,
                            bgcolor: selectedForexPlanId === plan.id ? `${Colors.Primary_50}` : "white",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            '&:hover': { borderColor: Colors.Primary_400 }
                          }}>
                            <CardContent>
                              <FormControlLabel
                                value={plan.id}
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {plan.label}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary">
                                      {plan.spreads} • {plan.leverage}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ width: "100%", alignItems: "flex-start" }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>

                  <Box sx={{ ml: 4, mt: 2, pl: 2, borderLeft: `3px solid ${Colors.Primary_300}` }}>
                    {hasSubOptions && selectedForexPlanId === "XLEVERAGE" && (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: Colors.Gray_700 }}>
                          Commission Options:
                        </Typography>
                        <RadioGroup value={selectedSubOptionId} onChange={(e) => setSelectedSubOptionId(e.target.value)}>
                          <Grid container spacing={1}>
                            {currentForexPlan.subOptions.options.map(opt => (
                              <Grid item xs={12} key={opt.id}>
                                <FormControlLabel
                                  value={opt.id}
                                  control={<Radio size="small" />}
                                  label={<Typography variant="body2">{opt.label}</Typography>}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>

                        {isUpfrontSelected && (
                          <>
                            <Typography variant="subtitle2" sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                              Commission Amount:
                            </Typography>
                            <FormControl fullWidth size="small" sx={{ mt: 1, maxWidth: 300 }} error={submittedOnce && !upfrontCommissionAmount}>
                              <Select
                                value={upfrontCommissionAmount}
                                onChange={(e) => setUpfrontCommissionAmount(e.target.value)}
                              >
                                {upfrontAllowedAmounts.map(amount => (
                                  <MenuItem key={amount} value={amount}>${amount}</MenuItem>
                                ))}
                              </Select>
                              {submittedOnce && !upfrontCommissionAmount && (
                                <FormHelperText error>Select a valid amount</FormHelperText>
                              )}
                            </FormControl>
                          </>
                        )}
                      </>
                    )}

                    <Box>
                      <Typography variant="subtitle2" sx={{ mt: 1, display: "flex", alignItems: "center" }}>
                        Trading Platform:
                      </Typography>
                      <FormControl fullWidth size="small" sx={{ maxWidth: 300 }}>
                        <Select
                          value={selectedPlatform}
                          onChange={(e) => setSelectedPlatform(e.target.value)}
                        >
                          {servers.map(platform => (
                            <MenuItem key={platform.value} value={platform.value}>{platform.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        Currency:
                      </Typography>
                      <FormControl fullWidth size="small" sx={{ maxWidth: 300 }}>
                        <Select
                          value={selectedCurrency}
                          onChange={(e) => setSelectedCurrency(e.target.value)}
                        >
                          {CURRENCY_OPTIONS.map(currency => (
                            <MenuItem key={currency.id} value={currency.id}>
                              {currency.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
              )}

              {selectedProductType === "equities" && (
                <Box sx={{ mb: 4 }}>                 
                  <RadioGroup value={selectedEquitiesPlanId} onChange={(e) => setSelectedEquitiesPlanId(e.target.value)}>
                    <Grid container spacing={2}>
                      {EQUITIES_PLANS.map(plan => (
                        <Grid item xs={12} sm={6} key={plan.id}>
                          <Card sx={{ 
                            border: selectedEquitiesPlanId === plan.id ? `2px solid ${Colors.Primary_600}` : `1px solid ${Colors.Gray_300}`,
                            bgcolor: selectedEquitiesPlanId === plan.id ? `${Colors.Primary_50}` : "white",
                            cursor: "pointer",
                            height: "100%",
                            transition: "all 0.2s",
                            '&:hover': { borderColor: Colors.Primary_400 }
                          }}>
                            <CardContent>
                              <FormControlLabel
                                value={plan.id}
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {plan.label}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: "block" }}>
                                      {plan.description}
                                    </Typography>
                                  </Box>
                                }
                                sx={{ alignItems: "flex-start", width: "100%" }}
                              />
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>                  
                </Box>
              )}

              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <CommentIcon fontSize="small" /> Additional Comments (optional):
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                  placeholder="Enter any additional comments or special requests here..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value.slice(0, 300))}
                  helperText={`${comment.length}/300 characters`}
                  sx={{ bgcolor: "white" }}
                />
              </Box>

              <Box sx={{ mt: 2, p: 1.5, bgcolor: Colors.Gray_50, borderRadius: "8px" }}>
                <Typography variant="caption" color="primary" sx={{ display: "block", textAlign: "center" }}>
                  * For customizable options beyond these presets, please contact our support team.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    fontWeight: 700,
                    padding: "10px 24px",
                    fontSize: "14px",
                    minWidth: 160,
                    backgroundColor: canSubmit ? Colors.Primary_600 : "#E5E7EB",
                    color: canSubmit ? "#fff" : "#6B7280",
                    "&:hover": {
                      backgroundColor: canSubmit ? Colors.Primary_700 : "#E5E7EB",
                    },
                  }}
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Box>
  );
}

export default DealTreeRequestPage;